import { Path, useForm } from "react-hook-form";
import { Box, Typography } from "@mui/material";
import Modal from "@mui/material/Modal";
import FormRow from "components/form/FormRow";
import { formatDateTime } from "utils";
import CancelButton from "components/button/CancelButton";
import ButtonComponent from "components/button/ButtonComponent";
import { useEffect, useMemo } from "react";
import { Category } from "../types";
import {
  CategoryCreateRequest,
  CategoryUpdateRequest,
  createCategoryApi,
  updateCategoryApi,
} from "../api/categoryApi";
import {
  CategoryFormData,
  CategoryFormField,
  getCategoryFormFields,
} from "../utils/categoryFormFields";
import CategoryDisplayOrderField from "./CategoryDisplayOrderField";

const styles = {
  field: {
    width: "100%",
    height: "48px",
    boxSizing: "border-box",
    backgroundColor: "var(--white)",
    borderRadius: 0,
    border: "3px solid var(--silver-150)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  disabledField: {
    width: "100%",
    height: "48px",
    boxSizing: "border-box",
    backgroundColor: "var(--offwhite-400)",
    borderRadius: 0,
    border: "3px solid var(--silver-150)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
};

interface ICategoryFormProps {
  type: "create" | "update";
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isLoading?: boolean;
  categoryDetail?: Category;
}

/**
 * CategoryFormModal Component
 *
 * This component renders a modal form for creating or updating category details.
 * It uses `react-hook-form` for form management and validation.
 * @param {ICategoryFormProps} props - The props for the component.
 * @param {"create" | "update"} props.type - Specifies whether the form is for creating or updating a category.
 * @param {boolean} props.visible - Controls the visibility of the modal.
 * @param {() => void} props.onClose - Callback function to close the modal.
 * @param {() => void} props.onSuccess - Callback function to handle successful form submission.
 * @param {boolean} [props.isLoading] - Indicates whether the form is in a loading state.
 * @param {Category} [props.categoryDetail] - Category details for pre-filling the form in update mode.
 */
export default function CategoryFormModal({
  type = "create",
  visible,
  onClose,
  onSuccess,
  isLoading = false,
  categoryDetail,
}: ICategoryFormProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      id: undefined,
      categoryCd: "",
      categoryKbnCd: "",
      categoryKbn: "",
      categoryName: "",
      seq: 1,
      statusFlag: 1,
      deleteFlag: 0,
    },
  });

  const isUpdate = type === "update";

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CategoryFormData) => {
    if (isLoading || Object.keys(errors).length > 0) return;

    try {
      if (isUpdate && categoryDetail) {
        const updateData: CategoryUpdateRequest = {
          id: categoryDetail.id,
          categoryCd: data.categoryCd,
          categoryKbnCd: data.categoryKbnCd,
          categoryKbn: data.categoryKbn,
          categoryName: data.categoryName,
          seq: data.seq,
          statusFlag: data.statusFlag,
          deleteFlag: data.deleteFlag,
        };
        await updateCategoryApi(updateData);
      } else {
        const createData: CategoryCreateRequest = {
          categoryCd: data.categoryCd,
          categoryKbnCd: data.categoryKbnCd,
          categoryKbn: data.categoryKbn,
          categoryName: data.categoryName,
          seq: data.seq,
          statusFlag: data.statusFlag,
          deleteFlag: data.deleteFlag,
        };
        await createCategoryApi(createData);
      }
      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error submitting category form:", error);
    }
  };

  // Get form fields configuration
  const categoryFormFields = useMemo(() => getCategoryFormFields(isUpdate), [isUpdate]);

  useEffect(() => {
    if (categoryDetail && isUpdate) {
      reset({
        id: categoryDetail.id,
        categoryCd: categoryDetail.category_cd,
        categoryKbnCd: categoryDetail.category_kbn_cd,
        categoryKbn: categoryDetail.category_kbn,
        categoryName: categoryDetail.category_name,
        seq: categoryDetail.seq,
        statusFlag: categoryDetail.status_flag,
        deleteFlag: categoryDetail.delete_flag,
      });
    }
  }, [categoryDetail, isUpdate, reset]);

  return (
    <Modal
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={visible}
      onClose={handleClose}
    >
      <Box
        sx={{
          maxHeight: "90vh",
          overflow: "auto",
          width: "80%",
          maxWidth: "870px",
          backgroundColor: "var(--white)",
          padding: { xs: 3, md: 4, xl: "30px 70px" },
          borderRadius: "21px",
          display: "flex",
          flexDirection: "column",
          rowGap: "20px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "fontWeightMedium",
              color: "var(--gray-700)",
              fontSize: { xs: 28, md: 33 },
            }}
          >
            {isUpdate ? "分類詳細" : "新規登録"}
            <Typography component="br" sx={{ display: { xs: "block", md: "none" } }} />
            {isUpdate && categoryDetail && (
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: 16, md: 21 },
                  fontWeight: "fontWeightMedium",
                  color: "var(--gray-700)",
                  ml: { xs: 0, md: 3 },
                }}
              >
                更新日 {formatDateTime(categoryDetail.updated_at)}
              </Typography>
            )}
          </Typography>
          <CancelButton onClose={handleClose} />
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          {categoryFormFields.map((field: CategoryFormField) => (
            <FormRow
              key={field.fieldName}
              sxLabel={{ pl: 4, width: "40%" }}
              label={field.label}
              sxBoxField={{ width: "60%", flexGrow: 1 }}
              sxField={styles.field}
              name={field.fieldName as Path<CategoryFormData>}
              type={field.type as never}
              control={control}
              disabled={field.disabled}
              rules={field.rules}
              options={field.options}
            />
          ))}

          {/* Custom display order field */}
          <CategoryDisplayOrderField
            control={control}
            seqName="seq"
            statusName="statusFlag"
            label="表示順"
            sxLabel={{ pl: 4, width: "40%" }}
            sxBoxField={{ width: "60%", flexGrow: 1 }}
            sxField={styles.field}
            disabledField={styles.disabledField}
          />

          <Box display="flex" columnGap="15px" marginTop="25px" alignItems="center">
            <ButtonComponent
              disabled={isLoading || Object.keys(errors).length > 0}
              type="submit"
              label={isUpdate ? "更新" : "登録"}
              theme="dark"
              sx={{ height: "50px", minWidth: "185px", width: "28%" }}
            />
          </Box>
        </form>

        <CancelButton sx={{ mt: 1.5 }} onClose={handleClose} />
      </Box>
    </Modal>
  );
}
